
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";

interface CreateClientData {
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  address?: string;
  notes?: string;
  birthDate?: string;
  gender?: 'male' | 'female' | 'other';
  weightKg?: number;
  heightCm?: number;
  amount?: number;
  subscriptions?: Array<{
    workoutTypeId: string;
    sessionsPerMonth: number;
    price: number; // Changed from pricePerSession to price
  }>;
}

export const useCreateClient = () => {
  const queryClient = useQueryClient();
  const {currentOrganization} = useAuth();
  const { t } = useTranslation();
  
  return useMutation({
    mutationFn: async (clientData: CreateClientData) => {
      console.log('Creating client with data:', clientData);

      if (!currentOrganization?.id) {
        throw new Error('User organization not found');
      }

      // Create client
      const { data: client, error: clientError } = await supabase
        .from('clients')
        .insert({
          first_name: clientData.firstName,
          last_name: clientData.lastName,
          email: clientData.email || null,
          phone: clientData.phone,
          address: clientData.address || null,
          amount: clientData.amount || 0,
          notes: clientData.notes || null,
          birth_date: clientData.birthDate || null,
          gender: clientData.gender || null,
          weight_kg: clientData.weightKg || null,
          height_cm: clientData.heightCm || null,
          organization_id: currentOrganization.id,
        })
        .select()
        .single();

      if (clientError) {
        console.error('Error creating client:', clientError);
        throw clientError;
      }

      console.log('Client created:', client);

      // Create subscriptions if provided
      if (clientData.subscriptions && clientData.subscriptions.length > 0) {
        const subscriptionsToInsert = clientData.subscriptions.map(sub => ({
          client_id: client.id,
          workout_type_id: sub.workoutTypeId,
          sessions_per_month: sub.sessionsPerMonth,
          price: sub.price, // Changed from price_per_session to price
          organization_id: currentOrganization.id,
        }));

        const { error: subscriptionsError } = await supabase
          .from('client_subscriptions')
          .insert(subscriptionsToInsert);

        if (subscriptionsError) {
          console.error('Error creating subscriptions:', subscriptionsError);
          throw subscriptionsError;
        }

        console.log('Subscriptions created for client:', client.id);
      }

      return client;
    },
    onSuccess: () => {
      // Invalidate all client-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['clients', currentOrganization?.id] });
      queryClient.invalidateQueries({ queryKey: ['clients-paginated', currentOrganization?.id] });
      queryClient.invalidateQueries({ queryKey: ['clients-infinite', currentOrganization?.id] });
      // Also invalidate dashboard stats that depend on clients data
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-clients'] });
      toast.success(t('clients.createClientSuccess'));
    },
    onError: (error: any) => {
      console.error('Error in create client mutation:', error);
      
      // Check for specific error codes
      if (error?.code === '23505' && error?.message?.includes('clients_email_key')) {
        toast.error(t('clients.duplicateEmailError'));
      } else {
        toast.error(t('clients.createClientError'));
      }
    }
  });
};
